/**
 * The scene behind the main menu, fetched after the menu is already on screen.
 *
 * The backdrop is the featured mode's own scene, so drawing it means downloading that mode. The
 * menu will not wait for that: cards, name and join row paint from the entry chunk, and the scenery
 * arrives when it arrives. A player on a slow connection reads the same menu a moment earlier
 * against its own background.
 *
 * The era counter lives here rather than in the shell because neither of the shell's guards fits.
 * `generation` is bumped when a session ends, which is not when a menu closes, and "is the menu
 * still up" is not it either: the lobby deliberately keeps this scene alive while the menu itself
 * is disposed. What matters is whether anybody still wants scenery, and that is exactly what `show`
 * and `hide` say.
 */

import { featuredMode } from '@/modes/modeRegistry';
import type { ModeBackdrop } from '@/modes/modeContract';

export interface MenuBackdrop {
  /** Mounts the featured mode's scene once its chunk lands. Does nothing while one is showing. */
  show(): void;
  /** Drops the scene, and disowns a load still in flight. */
  hide(): void;
}

export function createMenuBackdrop(container: HTMLElement): MenuBackdrop {
  let live: ModeBackdrop | null = null;
  let loading = false;
  // Bumped by every hide, so a chunk that lands after the player has left the menu mounts nothing.
  let era = 0;

  return {
    show(): void {
      if (live !== null || loading) return;
      const mode = featuredMode();
      if (mode?.hasBackdrop !== true) return;

      const wanted = era;
      loading = true;
      // A rejection is swallowed on purpose. This is scenery nobody asked for, and a menu that
      // announced a failed download of its own wallpaper would be reporting its own housekeeping.
      void mode
        .load()
        .then((code) => code.createBackdrop?.(container) ?? null)
        .then(
          (backdrop) => {
            loading = false;
            // A backdrop that lands after the player has left the menu is disposed on arrival:
            // nothing else knows it exists, so nothing else ever would.
            if (wanted !== era) {
              backdrop?.dispose();
              return;
            }
            live = backdrop;
          },
          () => {
            loading = false;
          },
        );
    },

    hide(): void {
      era += 1;
      loading = false;
      live?.dispose();
      live = null;
    },
  };
}
